import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { restaurantConfig } from '../config/restaurantConfig';
import SwiperTestimonials from './SwiperTestimonials';

interface TestimonialsProps {
  language: string;
}

interface ReviewProps {
  name: string;
  image: string;
  comment: {
    en: string;
    pt: string;
  };
  location: {
    en: string;
    pt: string;
  };
  rating: number;
  sourceUrl: string;
}

// Add proper interface for the ReviewModal props
interface ReviewModalProps {
  review: ReviewProps;
  language: string;
  onClose: () => void;
  renderStars: (rating: number) => string;
  handleImageError: (e: React.SyntheticEvent<HTMLImageElement, Event>, name: string) => void;
}

// Create a modal with ReactDOM portal for direct body mounting
const ReviewModal: React.FC<ReviewModalProps> = ({ review, language, onClose, renderStars, handleImageError }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    // Force modal to be visible
    if (modalRef.current) {
      modalRef.current.style.display = 'flex';
      modalRef.current.style.opacity = '1';
      modalRef.current.style.visibility = 'visible';
    }
    
    // Prevent scrolling
    document.body.style.overflow = 'hidden';
    
    return () => {
      // Re-enable scrolling when unmounted
      document.body.style.overflow = 'auto';
    };
  }, []);
  
  return ReactDOM.createPortal(
    <div className="review-modal-overlay" ref={modalRef} onClick={onClose}>
      <div className="review-modal" onClick={(e) => e.stopPropagation()} style={{ border: '1px solid var(--color-pink) !important', maxWidth: '750px' }}>
        <button className="review-modal-close" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
        <div className="review-modal-content" style={{ padding: '1.5rem' }}>
          <div className="review-stars" style={{ color: 'var(--color-pink)', marginBottom: '20px', fontSize: '20px' }}>
            {renderStars(review.rating)}
          </div>
          <p className="review-modal-text">
            {language === 'en' ? review.comment.en : review.comment.pt}
          </p>
          <div className="review-modal-author">
            <div className="author-image">
              <img 
                src={review.image} 
                alt={review.name} 
                onError={(e) => handleImageError(e, review.name)}
              />
            </div>
            <div className="author-info">
              <h4 className="author-name">{review.name}</h4>
              <span className="author-location">
                {language === 'en' ? review.location.en : review.location.pt}
              </span>
            </div>
          </div>
          <div className="review-source-link mt-4 pt-2 text-center">
            <a 
              href={review.sourceUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="view-on-google-btn"
              style={{ 
                backgroundColor: 'var(--color-pink)', 
                color: 'white',
                padding: '8px 16px',
                borderRadius: '4px',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                fontWeight: 500
              }}
            >
              <img src="/images/svg/Google__G__logo.svg" alt="Google" className="google-modal-icon" />
              {language === 'en' ? 'View on Google Maps' : 'Ver no Google Maps'}
            </a>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Testimonials: React.FC<TestimonialsProps> = ({ language }) => {
  const { testimonials } = restaurantConfig;
  const [selectedReview, setSelectedReview] = useState<ReviewProps | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loadedImages, setLoadedImages] = useState<{[key: string]: boolean}>({});

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Preload images
  React.useEffect(() => {
    testimonials.forEach(review => {
      const img = new Image();
      img.src = review.image;
      img.onload = () => {
        setLoadedImages(prev => ({...prev, [review.image]: true}));
      };
      img.onerror = () => {
        // After 2 seconds, if image hasn't loaded, mark as failed
        setTimeout(() => {
          setLoadedImages(prev => ({...prev, [review.image]: false}));
        }, 2000);
      };
    });
  }, [testimonials]);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>, name: string) => {
    const target = e.target as HTMLImageElement;
    const nameParts = name.split(' ');
    const initials = nameParts.length > 1 
      ? `${nameParts[0][0]}${nameParts[1][0]}` 
      : nameParts[0].substring(0, 2);
    
    // Generate a deterministic color based on the name
    const colors = ['#4285F4', '#DB4437', '#F4B400', '#0F9D58', '#9C27B0', '#FF6D00'];
    const colorIndex = name.length % colors.length;
    const backgroundColor = colors[colorIndex];
    
    // Create a canvas element
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      // Draw background circle
      ctx.fillStyle = backgroundColor;
      ctx.beginPath();
      ctx.arc(100, 100, 100, 0, 2 * Math.PI);
      ctx.fill();
      
      // Add text
      ctx.font = 'bold 80px Arial';
      ctx.fillStyle = '#FFFFFF';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(initials.toUpperCase(), 100, 100);
      
      // Replace the image source with the canvas data URL
      target.src = canvas.toDataURL('image/png');
    }
  };

  const openReviewModal = (review: any, event: React.MouseEvent) => {
    // Prevent default action and stop propagation to ensure the click always works
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Set the selected review directly in state - this should trigger modal rendering
    setSelectedReview(review);
    
    // Prevent scrolling when modal is open
    document.body.style.overflow = 'hidden';
    
    // Ensure modal is visible in case CSS is hiding it
    setTimeout(() => {
      const modalOverlay = document.querySelector('.review-modal-overlay');
      if (modalOverlay) {
        (modalOverlay as HTMLElement).style.display = 'flex';
        (modalOverlay as HTMLElement).style.opacity = '1';
        (modalOverlay as HTMLElement).style.visibility = 'visible';
      }
    }, 10);
  };

  const closeReviewModal = () => {
    // Simply reset state - the portal component handles its own cleanup
    setSelectedReview(null);
  };

  const renderStars = (rating: number) => {
    return "★".repeat(rating);
  };

  return (
    <section className="py-12 bg-gray-100" id="testimonials">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold mb-4 font-fleur">
            {language === 'en' ? 'Customer Reviews' : 'Avaliações dos Clientes'}
          </h2>
          <div className="w-24 h-1 bg-[var(--color-pink)] mx-auto"></div>
        </div>
        
        {isMobile ? (
          <>
            <SwiperTestimonials
              reviews={testimonials}
              language={language}
              onReviewClick={openReviewModal}
            />
            <div className="swipe-hint text-center text-sm text-gray-500 mt-4">
              <p>
                <i className="fas fa-hand-point-right mr-2"></i>
                {language === 'en' ? 'Swipe to see more reviews' : 'Deslize para ver mais avaliações'}
              </p>
            </div>
          </>
        ) : (
          <div className="testimonials-carousel">
            {testimonials.map((review, index) => (
              <div 
                key={`review-${index}`} 
                className="testimonial-card"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openReviewModal(review, e);
                }}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    openReviewModal(review, e as any);
                  }
                }}
              >
                <div className="testimonial-content">
                  <div className="review-stars">
                    <span style={{ color: 'var(--color-pink)' }}>{renderStars(review.rating)}</span>
                    <a 
                      href={review.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="google-source-indicator" 
                      title={language === 'en' ? 'View on Google Maps' : 'Ver no Google Maps'}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <img src="/images/svg/Google__G__logo.svg" alt="Google" className="google-icon" />
                    </a>
                  </div>
                  <div className="testimonial-text-container">
                    <p className="testimonial-text">
                      {language === 'en' ? review.comment.en : review.comment.pt}
                    </p>
                    <button 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openReviewModal(review, e);
                      }} 
                      className="read-more-btn"
                      aria-label="Read full review"
                    >
                      {language === 'en' ? 'Read More' : 'Ler Mais'}
                    </button>
                  </div>
                </div>
                <div className="testimonial-author">
                  <div className="author-image">
                    <img 
                      src={review.image} 
                      alt={review.name} 
                      onError={(e) => handleImageError(e, review.name)}
                    />
                  </div>
                  <div className="author-info">
                    <h4 className="author-name">{review.name}</h4>
                    <span className="author-location">{language === 'en' ? review.location.en : review.location.pt}</span>
                  </div>
                </div>
              </div>
            ))}
            
            {testimonials.map((review, index) => (
              <div 
                key={`review-duplicate-${index}`} 
                className="testimonial-card"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openReviewModal(review, e);
                }}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    openReviewModal(review, e as any);
                  }
                }}
              >
                <div className="testimonial-content">
                  <div className="review-stars">
                    <span style={{ color: 'var(--color-pink)' }}>{renderStars(review.rating)}</span>
                    <a 
                      href={review.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="google-source-indicator" 
                      title={language === 'en' ? 'View on Google Maps' : 'Ver no Google Maps'}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <img src="/images/svg/Google__G__logo.svg" alt="Google" className="google-icon" />
                    </a>
                  </div>
                  <div className="testimonial-text-container">
                    <p className="testimonial-text">
                      {language === 'en' ? review.comment.en : review.comment.pt}
                    </p>
                    <button 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openReviewModal(review, e);
                      }} 
                      className="read-more-btn"
                      aria-label="Read full review"
                    >
                      {language === 'en' ? 'Read More' : 'Ler Mais'}
                    </button>
                  </div>
                </div>
                <div className="testimonial-author">
                  <div className="author-image">
                    <img 
                      src={review.image} 
                      alt={review.name} 
                      onError={(e) => handleImageError(e, review.name)}
                    />
                  </div>
                  <div className="author-info">
                    <h4 className="author-name">{review.name}</h4>
                    <span className="author-location">{language === 'en' ? review.location.en : review.location.pt}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div className="text-center mt-10">
          <a 
            href="https://www.google.pt/maps/place/Berlim/@37.115312,-8.6529148,118m/data=!3m1!1e3!4m18!1m9!3m8!1s0xd1b31db6856fde7:0x643f61d3e78b0a58!2sBerlim!8m2!3d37.1152731!4d-8.6525974!9m1!1b1!16s%2Fg%2F1hc6ygbm3!3m7!1s0xd1b31db6856fde7:0x643f61d3e78b0a58!8m2!3d37.1152731!4d-8.6525974!9m1!1b1!16s%2Fg%2F1hc6ygbm3?entry=ttu&g_ep=EgoyMDI1MDQwOC4wIKXMDSoASAFQAw%3D%3D" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-[var(--color-pink)] text-white rounded-full hover:bg-opacity-90 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 font-bold animate-pulse-subtle hover:text-white"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
            </svg>
            {language === 'en' ? 'Read More Reviews on Google' : 'Leia Mais Avaliações no Google'}
          </a>
        </div>

        {/* Book a Table CTA */}
        <div className="text-center mt-12">
          <a 
            href="tel:+351282763777" 
            className="inline-flex items-center justify-center px-6 py-3 bg-[#C55E86] text-white rounded-full hover:bg-opacity-90 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 font-bold"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            {language === 'en' ? 'Book a Table' : 'Reserve uma Mesa'}
          </a>
        </div>
      </div>

      {/* Replace the inline modal with the portal component */}
      {selectedReview && (
        <ReviewModal 
          review={selectedReview}
          language={language}
          onClose={closeReviewModal}
          renderStars={renderStars}
          handleImageError={handleImageError}
        />
      )}

      <style>{`
        /* CSS Styles */
        .testimonials-carousel {
          display: flex;
          padding: 1rem 0;
          -webkit-overflow-scrolling: touch;
          scroll-snap-type: x mandatory;
          overflow-x: auto;
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE and Edge */
          justify-content: flex-start;
          gap: 1.5rem;
          padding: 1rem 2rem 1.5rem;
          margin: 0 -1rem;
        }

        .testimonials-carousel::-webkit-scrollbar {
          display: none; /* Chrome, Safari, Opera */
        }

        /* Swiper styles */
        .testimonials-swiper {
          width: 100%;
          padding: 0 10px;
          margin-bottom: 1rem;
        }

        .testimonials-swiper .swiper-slide {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.5rem 0;
          transition: transform 0.8s ease, opacity 0.8s ease;
        }

        .testimonials-swiper .swiper-slide-active {
          transform: scale(1.05);
          z-index: 1;
        }

        .testimonials-swiper .swiper-slide-prev,
        .testimonials-swiper .swiper-slide-next {
          opacity: 0.7;
        }

        .testimonials-swiper .swiper-pagination-bullet {
          width: 8px;
          height: 8px;
          transition: all 0.3s ease;
        }

        .testimonials-swiper .swiper-pagination-bullet-active {
          background-color: var(--color-pink);
          transform: scale(1.3);
        }

        /* Custom pagination container */
        .testimonials-pagination {
          display: flex;
          justify-content: center;
          margin-top: 20px;
          padding-bottom: 10px;
        }
        
        .testimonials-pagination .swiper-pagination-bullet {
          margin: 0 5px;
        }

        /* Swipe Hint */
        .swipe-hint {
          text-align: center;
          color: #777;
          margin: 5px 0 20px;
          font-size: 0.9rem;
          opacity: 0.8;
          animation: fadeInUp 1s ease forwards;
        }

        .swipe-hint i {
          animation: swipeHint 2s infinite;
          display: inline-block;
        }

        @keyframes swipeHint {
          0%, 100% {
            transform: translateX(0);
          }
          50% {
            transform: translateX(5px);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 0.8;
            transform: translateY(0);
          }
        }

        @keyframes pulseSubtle {
          0% { transform: scale(1); box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); }
          50% { transform: scale(1.03); box-shadow: 0 8px 15px rgba(0, 0, 0, 0.15); }
          100% { transform: scale(1); box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); }
        }

        .animate-pulse-subtle {
          animation: pulseSubtle 3s infinite;
        }

        .testimonial-card {
          flex: 0 0 auto;
          width: 350px;
          background-color: white;
          border-radius: 8px;
          border: 1px solid var(--color-pink);
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          min-height: 280px;
          max-height: 320px;
          height: auto;
        }
        
        .testimonial-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
          border: 1px solid var(--color-dark-pink);
        }

        .testimonial-card:focus {
          border-color: #000;
        }

        .testimonial-content {
          padding: 0.85rem 1rem;
          flex-grow: 1;
          display: flex;
          flex-direction: column;
        }

        .testimonial-text-container {
          margin-top: 0.5rem;
          flex-grow: 1;
        }

        .testimonial-text {
          font-size: 0.92rem;
          line-height: 1.5;
          color: #4a4a4a;
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          margin-bottom: 0.5rem;
          height: 90px;
        }

        .read-more-btn {
          color: var(--color-pink);
          background: none;
          border: none;
          padding: 0;
          font: inherit;
          cursor: pointer;
          text-decoration: underline;
          font-size: 0.85rem;
          font-weight: 500;
          margin-top: 5px;
          line-height: 1;
          visibility: visible;
          opacity: 1;
        }

        .read-more-btn:hover {
          color: var(--color-green);
        }

        .testimonial-author {
          padding: 0.6rem 1rem;
          border-top: 1px solid #f0f0f0;
          background-color: white;
          margin-top: auto;
          display: flex;
          align-items: center;
        }

        .author-image {
          width: 34px;
          height: 34px;
          min-width: 34px;
          min-height: 34px;
          border-radius: 50%;
          overflow: hidden;
          margin-right: 0.75rem;
          flex-shrink: 0;
          background-color: #f0f0f0;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .author-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          position: absolute;
          top: 0;
          left: 0;
        }

        .author-info {
          flex-grow: 1;
        }

        .author-name {
          font-weight: 600;
          color: #333;
          margin: 0;
          font-size: 0.9rem;
        }

        .author-location {
          color: #777;
          font-size: 0.8rem;
        }

        .review-stars {
          color: var(--color-pink) !important;
        }

        .google-source-indicator {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          position: absolute;
          right: 0;
          top: 0;
        }

        .google-icon {
          width: 20px;
          height: 20px;
          object-fit: contain;
        }

        .google-modal-icon {
          width: 20px;
          height: 20px;
          object-fit: contain;
          margin-right: 8px;
        }

        /* Modal Styles - with !important flags to override anything */
        .review-modal-overlay {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          background-color: rgba(0, 0, 0, 0.7) !important;
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          z-index: 9999 !important;
          opacity: 1 !important;
          visibility: visible !important;
          pointer-events: auto !important;
        }

        .review-modal {
          background-color: white !important;
          border-radius: 8px !important;
          border: 1px solid var(--color-pink) !important;
          width: 100% !important;
          max-width: 600px !important;
          max-height: 90vh !important;
          overflow-y: auto !important;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3) !important;
          position: relative !important;
          opacity: 1 !important;
          visibility: visible !important;
          transform: none !important;
          transition: none !important;
        }

        .review-modal-close {
          position: absolute !important;
          top: 15px !important;
          right: 15px !important;
          background: none !important;
          border: none !important;
          font-size: 20px !important;
          color: #666 !important;
          cursor: pointer !important;
          z-index: 10 !important;
        }

        .review-modal-content {
          padding: 30px !important;
        }

        .review-modal .review-stars {
          margin-bottom: 20px;
          color: var(--color-pink);
          font-size: 20px;
        }

        .review-modal-text {
          font-size: 16px;
          line-height: 1.7;
          margin-bottom: 20px;
          color: #4a4a4a;
        }

        .review-modal-author {
          display: flex;
          align-items: center;
          padding-top: 20px;
          border-top: 1px solid #eee;
          background-color: white;
        }

        .review-modal-author .author-image {
          width: 60px;
          height: 60px;
          min-width: 60px;
          min-height: 60px;
          background-color: #f0f0f0;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          border-radius: 50%;
          position: relative;
        }

        .review-modal-author .author-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          position: absolute;
          top: 0;
          left: 0;
        }

        .review-modal-author .author-name {
          font-weight: 600;
          font-size: 18px;
        }

        .review-modal-author .author-location {
          color: #777;
        }

        .author-initials {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 14px;
          color: #fff;
          background-color: var(--color-pink);
        }

        .review-modal-author .author-initials {
          font-size: 24px;
        }

        .view-on-google-btn {
          display: inline-flex;
          align-items: center;
          background-color: var(--color-pink);
          color: white;
          border: none;
          border-radius: 4px;
          padding: 8px 16px;
          font-size: 0.9rem;
          font-weight: 500;
          text-decoration: none;
          transition: all 0.3s ease;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          margin-top: 15px;
        }

        .view-on-google-btn:hover {
          background-color: var(--color-green);
          text-decoration: none;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }

        .view-on-google-btn i {
          margin-right: 8px;
        }

        .review-source-link {
          margin-top: 1rem;
          border-top: 1px dashed #eee;
          padding-top: 1rem;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .testimonials-carousel {
            justify-content: flex-start;
            padding-left: 1rem;
            padding-right: 1rem;
            gap: 0;
            margin: 0;
          }
          .testimonial-card {
            width: 280px;
            flex-shrink: 0;
            scroll-snap-align: center;
            margin-right: 16px;
          }
          
          /* Ensure the last card has right margin */
          .testimonial-card:last-child {
            margin-right: 16px;
          }
        }

        @media (max-width: 480px) {
          .testimonials-carousel {
            padding-left: 1rem;
            padding-right: 0;
          }
          
          .testimonial-card {
            width: 260px;
            margin-right: 15px;
          }

          .review-modal-content {
            padding: 20px;
          }
        }
      `}</style>
    </section>
  );
};

export default Testimonials; 